import _ from 'lodash';
import template from 'url-template';

import { saveAs } from 'file-saver';
import { decode } from 'base64-arraybuffer';

import {
    AGENT_REORDER,
    FETCH_BAS_AGENTS,
    FETCH_BAS_AGENTS_REPORT,
    SET_AGENT,
    SET_SELECTED_AGENT
} from './actionTypes';
import { handleNetworkError, enqueueSnackbar, clearErrors } from './applicationMessageActions';
import { setBobLoading } from './bobActions';

import awsHeader from '../helpers/awsAuth';
import { getSortOrder } from '../helpers/collections';
import k2aBaseApi from '../apis/key2act';

const DEFAULT_AGENTS_LIMIT = 100;

const BasAgentUrl = template.parse('/basagents/{agentId}');
const CompanyBasAgentsUrl = template.parse(
    '/companies/{companyId}/basagents{?limit,page,searchFilter,sessionId,sort,isSuperAdmin}'
);
const PingBasAgentUrl = template.parse('/basagents/{agentId}/ping{?sessionId}');
const RebootBasAgentUrl = template.parse('/basagents/{agentId}/reboot{?sessionId}');
const ResetBasAgentUrl = template.parse('/basagents/{agentId}/factory-reset{?sessionId}');
const RestartBasAgentUrl = template.parse('/basagents/{agentId}/restart{?sessionId}');
const AgentSiteLinkUrl = template.parse('/basagents/{agentId}/site/{siteId}');
const FileDownloadUrl = template.parse('/downloads/software-agent-container{?fileType}');

const ProvisionSoftwareUrl = template.parse('/companies/{companyId}/software-basagent-provision');

export const createBasAgent = (companyId, agentData, linkedSites) => async dispatch => {
    const header = await awsHeader();
    let body = null;
    try {

        const response = await k2aBaseApi.post(
            CompanyBasAgentsUrl.expand({ companyId }),
            agentData,
            header
        );

        const agentId = _.get(response, 'data.id', '');

        let promises = [];
        if (agentId !== '') {
            linkedSites.forEach(site => {
                const siteId = site.siteId;
                promises.push(
                    k2aBaseApi.post(
                        AgentSiteLinkUrl.expand({ agentId,siteId }),
                        body,
                        header
                    ));
                
            });
        }

        await Promise.all(promises.map(p => p.catch(err => err)));

        await Promise.all([
            dispatch(enqueueSnackbar('Agent created successfully.', 'success')),
            dispatch(fetchBasAgents(companyId))
        ]);

        return true;
    } catch (error) {
        await dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateBasAgent = (companyId, agentId, agentData) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.patch(
            BasAgentUrl.expand({ agentId }),
            agentData,
            header
        );

        const response = await k2aBaseApi.get(`/basagents/${agentId}`, header);
        const payload = _.get(response, 'data', {});

        await dispatch(setAgent(payload));
        await dispatch(fetchBasAgents(companyId));

        dispatch(enqueueSnackbar('Agent updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteBasAgent = (companyId, agentId, agentData) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            BasAgentUrl.expand({ agentId }),
            {
                data:    agentData,
                headers: header.headers
            }
        );

        await Promise.all([
            dispatch(enqueueSnackbar('Agent deleted successfully.', 'success')),
            dispatch(fetchBasAgents(companyId))
        ]);

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchBasAgents = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { auth: { instanceId: sessionId } } = getState();
    options.limit || (options.limit = DEFAULT_AGENTS_LIMIT);

    dispatch(setBobLoading('FetchBasAgents'));
    try {
        const response = await k2aBaseApi.get(
            CompanyBasAgentsUrl.expand({
                companyId,
                sessionId,
                ...options
            }),
            header
        );
        let payload = _.get(response.data, 'data', []);

        payload = _.orderBy(payload, [agent => agent.name.toLowerCase()], ['asc']);

        dispatch({
            type: FETCH_BAS_AGENTS,
            payload
        });
        dispatch(setBobLoading('FetchBasAgents'));
    } catch (error) {
        dispatch(setBobLoading('FetchBasAgents'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchBasAgentsReport = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { auth: { instanceId: sessionId } } = getState();
    options.limit || (options.limit = DEFAULT_AGENTS_LIMIT);
    let totalPayload = [];
    let totalCount = 0;

    dispatch(setBobLoading('FetchBasAgents'));
    try {
        const getPayload = async (page = 1) => {
            options.page = page;
            const response = await k2aBaseApi.get(
                CompanyBasAgentsUrl.expand({
                    companyId,
                    sessionId,
                    ...options
                }),
                header
            );

            const meta = _.get(response, 'data.meta', '');
            const payload = _.get(response.data, 'data', []);
            totalCount = meta.totalCount;

            payload.forEach(p => {
                p.driverType = p.driverConfiguration[0].basType;
            });
            totalPayload = [...totalPayload, ...payload];

            page++;
            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await (getPayload(page));
            }
        };

        await getPayload();

        totalPayload = _.orderBy(totalPayload, [agent => agent.name.toLowerCase()], ['asc']);

        dispatch({
            type:    FETCH_BAS_AGENTS_REPORT,
            payload: totalPayload
        });
        dispatch(setBobLoading('FetchBasAgents'));
    } catch (error) {
        dispatch(setBobLoading('FetchBasAgents'));
        dispatch(handleNetworkError(error));
    }
};

export const linkAgentsToSites = (companyId, agentId, linkedSites) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        let promises = [];
        if (agentId !== '') {
            linkedSites.forEach(siteId => {
                promises.push(
                    k2aBaseApi.post(
                        AgentSiteLinkUrl.expand({agentId, siteId}),
                        null,
                        header
                    )
                );
            });
        }

        await Promise.all(promises.map(p => p));

        dispatch(enqueueSnackbar('Sites linked to agent successfully.', 'success'));

        return true;
    }
    catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const unlinkAgentsFromSites = (companyId, agentId, linkedSites) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        let promises = [];
        if (agentId !== '') {
            linkedSites.forEach(siteId => {
                promises.push(
                    k2aBaseApi.delete(
                        AgentSiteLinkUrl.expand({agentId, siteId}),
                        header
                    )
                );
            });
        }

        await Promise.all(promises.map(p => p));

        dispatch(enqueueSnackbar('Sites unlinked from agent successfully.', 'success'));

        return true;
    }
    catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }

};

export const clearBasAgentsReport = () => ({
    type:    FETCH_BAS_AGENTS_REPORT,
    payload: []
});

export const fetchAgentById = agentId => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            BasAgentUrl.expand({
                agentId
            }),
            header
        );
        const payload = _.get(response, 'data', {});
        dispatch(setAgent(payload));

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return {};
    }
};

export const fetchBasAgent = agentId => async (dispatch, getState) => {
    const header = await awsHeader();
    const { basAgent: { basAgents, selectedAgentId } } = getState();
    const selectedAgent = basAgents[selectedAgentId];

    if ((agentId === selectedAgentId) && !_.isNil(selectedAgent)) {
        return selectedAgent;
    }

    try {
        const response = await k2aBaseApi.get(
            BasAgentUrl.expand({
                agentId
            }),
            header
        );
        const payload = _.get(response, 'data', {});
        dispatch(setAgent(payload));

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return {};
    }
};

const doAgentAction = (url, agentId, notification) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { auth: { instanceId: sessionId } } = getState();
    try {
        await k2aBaseApi.post(
            url.expand({
                agentId,
                sessionId
            }), null, header
        );

        dispatch(enqueueSnackbar(notification, 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
    finally {
        dispatch(clearErrors());
    }
};

export const pingBasAgent = agentId => doAgentAction(PingBasAgentUrl, agentId, 'Agent ping initiated.');
export const rebootAgent = agentId => doAgentAction(RebootBasAgentUrl, agentId, 'Agent reboot initiated.');
export const restartAgentApplication = (agentId) => doAgentAction(RestartBasAgentUrl, agentId, 'Application restart initiated.');
export const factoryResetAgent = (agentId) => doAgentAction(ResetBasAgentUrl, agentId, 'Factory reset initiated.');

// Awaiting development of the device scan feature before completing this action creator
export const scanBacnetDevices = (basAgentId, agentData) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(`/basagents/${basAgentId}/device-scan`, agentData, header);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const reorderAgents = (newOrderBy) => async (dispatch, getState) => {
    const currentState = getState().basAgent;
    const order = getSortOrder(newOrderBy, currentState);
    dispatch({
        type:    AGENT_REORDER,
        payload: {
            order,
            orderBy: newOrderBy
        }
    });
};

export const provisionSoftwareAgent = companyId => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.post(
            ProvisionSoftwareUrl.expand({ companyId }),
            {},
            header
        );
        const payload = _.get(response, 'data');

        const stringPayload = JSON.stringify(payload);

        const blob = new Blob([stringPayload], { type: 'application/json' });
        saveAs(blob, 'Credentials');
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const downloadGatewayManagerTool = () => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(`/downloads/gateway-manager-tool`, header);

        const payload = _.get(response, 'data');

        window.open(payload);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const downloadSoftwareAgentContainer = (fileType) => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            FileDownloadUrl.expand({ fileType }),
            header
        );

        let payload = decode(_.get(response, 'data'));
        const blob = new Blob([payload], { type: 'application/zip' });
        saveAs(blob, 'Software_BAS_Agent');

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setAgent = agent => async (dispatch, getState) => {
    dispatch({
        type:    SET_AGENT,
        payload: agent
    });
};

export const setSelectedAgent = agent => async (dispatch, getState) => {
    dispatch({
        type:    SET_SELECTED_AGENT,
        payload: agent
    });
};

export const setBasAgents = agents => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_BAS_AGENTS,
        payload: agents
    });
};
